import React, {useCallback, useMemo, useRef} from 'react'
import {Editor} from '@tiptap/react'
import IconButton from '@material-ui/core/IconButton'
import Tooltip from '@material-ui/core/Tooltip'
import FormatBoldIcon from '@material-ui/icons/FormatBold'
import FormatItalicIcon from '@material-ui/icons/FormatItalic'
import FormatUnderlinedIcon from '@material-ui/icons/FormatUnderlined'
import StrikethroughSIcon from '@material-ui/icons/StrikethroughS'
import FormatListBulletedIcon from '@material-ui/icons/FormatListBulleted'
import FormatListNumberedIcon from '@material-ui/icons/FormatListNumbered'
import FormatQuoteIcon from '@material-ui/icons/FormatQuote'
import FormatAlignLeftIcon from '@material-ui/icons/FormatAlignLeft'
import FormatAlignCenterIcon from '@material-ui/icons/FormatAlignCenter'
import FormatAlignRightIcon from '@material-ui/icons/FormatAlignRight'
import FormatAlignJustifyIcon from '@material-ui/icons/FormatAlignJustify'
import FormatClearIcon from '@material-ui/icons/FormatClear'
import LinkIcon from '@material-ui/icons/Link'
import ImageIcon from '@material-ui/icons/Image'
import VideoLibraryIcon from '@material-ui/icons/VideoLibrary'
import CodeIcon from '@material-ui/icons/Code'
import FormatIndentIncreaseIcon from '@material-ui/icons/FormatIndentIncrease'
import FormatIndentDecreaseIcon from '@material-ui/icons/FormatIndentDecrease'
import TableChartIcon from '@material-ui/icons/TableChart'
import {ToolbarItem, ObvioVariable, EditorTheme, CUSTOM_LINK_MAP} from './types'
import CustomButtonToolbar from './CustomButtonToolbar'

type MenuBarProps = {
  editor: Editor
  onImageUpload: (file: File) => void
  toolbar: ToolbarItem[]
  variables?: ObvioVariable[]
  customLinks?: string[]
  theme: EditorTheme
  onThemeChange: (theme: EditorTheme) => void
}

export default function MenuBar({
  editor,
  onImageUpload,
  toolbar,
  variables,
  customLinks,
  theme,
  onThemeChange,
}: MenuBarProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)

  const has = useMemo(() => {
    const set = new Set<ToolbarItem>(toolbar)
    return (item: ToolbarItem) => set.has(item)
  }, [toolbar])

  const handleImageClick = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file) {
        onImageUpload(file)
      }
      // Reset so same file can be re-selected
      e.target.value = ''
    },
    [onImageUpload],
  )

  const handleLink = useCallback(() => {
    const previousUrl = editor.getAttributes('link').href

    if (customLinks && customLinks.length > 0) {
      const presetOptions = customLinks
        .map((name) => {
          const href = CUSTOM_LINK_MAP[name]
          if (href) {
            return `${name} (${href})`
          }
          return null
        })
        .filter(Boolean)
        .join('\n')

      const prompt = `Enter URL or choose a preset:\n${presetOptions}\n\nType a preset name or enter a URL:`
      const input = window.prompt(prompt, previousUrl)

      if (input === null) {
        return
      }

      if (input === '') {
        editor.chain().focus().extendMarkRange('link').unsetLink().run()
        return
      }

      // Check if input matches a preset name
      const matchedPreset = customLinks.find(
        (name) => input.toLowerCase() === name.toLowerCase(),
      )
      const href = matchedPreset ? CUSTOM_LINK_MAP[matchedPreset] : input

      editor.chain().focus().extendMarkRange('link').setLink({href}).run()
    } else {
      const url = window.prompt('URL', previousUrl)

      if (url === null) {
        return
      }

      if (url === '') {
        editor.chain().focus().extendMarkRange('link').unsetLink().run()
        return
      }

      editor.chain().focus().extendMarkRange('link').setLink({href: url}).run()
    }
  }, [editor, customLinks])

  const handleMediaEmbed = useCallback(() => {
    const url = window.prompt('Enter media URL (YouTube, Vimeo, Wistia, etc.)')
    if (url) {
      editor.chain().focus().setMediaEmbed({src: url}).run()
    }
  }, [editor])

  const handleHtmlEmbed = useCallback(() => {
    const html = window.prompt('Enter HTML code')
    if (html) {
      editor.chain().focus().setHtmlEmbed({html}).run()
    }
  }, [editor])

  const handleInsertTable = useCallback(() => {
    editor
      .chain()
      .focus()
      .insertTable({rows: 3, cols: 3, withHeaderRow: true})
      .run()
  }, [editor])

  const handleInsertCustomButton = useCallback(() => {
    editor.chain().focus().insertCustomButton().run()
  }, [editor])

  const handleVariableChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value
      if (value) {
        editor.chain().focus().insertObvioVariable(value).run()
      }
      // Reset select
      e.target.value = ''
    },
    [editor],
  )

  const currentFontFamily = editor.getAttributes('textStyle').fontFamily || ''
  const currentFontSize = editor.getAttributes('textStyle').fontSize || ''
  const currentFontColor = editor.getAttributes('textStyle').color || '#000000'
  const currentHighlight = editor.getAttributes('highlight').color || '#ffff00'

  const showHeadingGroup =
    has('heading') || has('customFontFamily') || has('customFontSize')
  const showColorGroup = has('fontColor') || has('fontBackgroundColor')
  const showAlignmentGroup = has('alignment')
  const showFormattingGroup =
    has('bold') ||
    has('italic') ||
    has('underline') ||
    has('strikethrough') ||
    has('superscript') ||
    has('removeFormat')
  const showInsertGroup =
    has('link') || has('mediaEmbed') || has('imageUpload') || has('htmlEmbed')
  const showListGroup =
    has('bulletedList') ||
    has('numberedList') ||
    has('outdent') ||
    has('indent')
  const showBlockGroup = has('blockQuote') || has('insertTable')
  const showVariableGroup =
    has('obvioVariable') && variables && variables.length > 0
  const showCustomButtonGroup = has('insertCustomButton')
  const showThemeGroup = has('customTheme')

  const isCustomButtonSelected =
    editor.state.doc.nodeAt(editor.state.selection.from)?.type.name ===
    'customButton'

  return (
    <>
      <div className="html-editor-toolbar">
        {/* Heading / Font / Size */}
        {showHeadingGroup && (
          <div className="html-editor-toolbar__group">
            {has('heading') && (
              <select
                className="html-editor-toolbar__select"
                value={
                  editor.isActive('heading', {level: 1})
                    ? '1'
                    : editor.isActive('heading', {level: 2})
                    ? '2'
                    : editor.isActive('heading', {level: 3})
                    ? '3'
                    : editor.isActive('heading', {level: 4})
                    ? '4'
                    : '0'
                }
                onChange={(e) => {
                  const level = parseInt(e.target.value)
                  if (level === 0) {
                    editor.chain().focus().setParagraph().run()
                  } else {
                    editor
                      .chain()
                      .focus()
                      .toggleHeading({level: level as 1 | 2 | 3 | 4})
                      .run()
                  }
                }}
              >
                <option value="0">Paragraph</option>
                <option value="1">Heading 1</option>
                <option value="2">Heading 2</option>
                <option value="3">Heading 3</option>
                <option value="4">Heading 4</option>
              </select>
            )}

            {has('heading') &&
              (has('customFontFamily') || has('customFontSize')) && (
                <div className="html-editor-toolbar__divider" />
              )}

            {has('customFontFamily') && (
              <select
                className="html-editor-toolbar__select"
                value={currentFontFamily}
                onChange={(e) => {
                  const value = e.target.value
                  if (value === '') {
                    editor.chain().focus().unsetFontFamily().run()
                  } else if (value === '__custom__') {
                    const custom = window.prompt('Enter font family name')
                    if (custom) {
                      editor.chain().focus().setFontFamily(custom).run()
                    }
                  } else {
                    editor.chain().focus().setFontFamily(value).run()
                  }
                }}
              >
                <option value="">Default Font</option>
                <option value="Arial, sans-serif">Arial</option>
                <option value="'Courier New', monospace">Courier New</option>
                <option value="Georgia, serif">Georgia</option>
                <option value="'Times New Roman', serif">
                  Times New Roman
                </option>
                <option value="Verdana, sans-serif">Verdana</option>
                <option value="__custom__">Custom...</option>
              </select>
            )}

            {has('customFontSize') && (
              <input
                className="html-editor-toolbar__size-input"
                type="number"
                min="1"
                max="200"
                placeholder="Size"
                value={currentFontSize ? parseInt(currentFontSize) : ''}
                onChange={(e) => {
                  const value = e.target.value
                  if (!value) {
                    editor.chain().focus().unsetFontSize().run()
                  } else {
                    editor.chain().focus().setFontSize(`${value}px`).run()
                  }
                }}
              />
            )}
          </div>
        )}

        {/* Colors */}
        {showColorGroup && (
          <div className="html-editor-toolbar__group">
            {has('fontColor') && (
              <Tooltip title="Font Color">
                <label className="html-editor-toolbar__color-label">
                  <span
                    className="html-editor-toolbar__color-icon"
                    style={{color: currentFontColor}}
                  >
                    A
                  </span>
                  <input
                    type="color"
                    className="html-editor-toolbar__color-input"
                    value={currentFontColor}
                    onChange={(e) => {
                      editor.chain().focus().setColor(e.target.value).run()
                    }}
                  />
                </label>
              </Tooltip>
            )}

            {has('fontBackgroundColor') && (
              <Tooltip title="Background Color">
                <label className="html-editor-toolbar__color-label">
                  <span
                    className="html-editor-toolbar__color-icon html-editor-toolbar__color-icon--bg"
                    style={{backgroundColor: currentHighlight}}
                  >
                    A
                  </span>
                  <input
                    type="color"
                    className="html-editor-toolbar__color-input"
                    value={currentHighlight}
                    onChange={(e) => {
                      editor
                        .chain()
                        .focus()
                        .toggleHighlight({color: e.target.value})
                        .run()
                    }}
                  />
                </label>
              </Tooltip>
            )}
          </div>
        )}

        {/* Alignment */}
        {showAlignmentGroup && (
          <div className="html-editor-toolbar__group">
            <Tooltip title="Align Left">
              <IconButton
                size="small"
                onClick={() =>
                  editor.chain().focus().setTextAlign('left').run()
                }
                color={
                  editor.isActive({textAlign: 'left'}) ? 'primary' : 'default'
                }
              >
                <FormatAlignLeftIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Align Center">
              <IconButton
                size="small"
                onClick={() =>
                  editor.chain().focus().setTextAlign('center').run()
                }
                color={
                  editor.isActive({textAlign: 'center'}) ? 'primary' : 'default'
                }
              >
                <FormatAlignCenterIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Align Right">
              <IconButton
                size="small"
                onClick={() =>
                  editor.chain().focus().setTextAlign('right').run()
                }
                color={
                  editor.isActive({textAlign: 'right'}) ? 'primary' : 'default'
                }
              >
                <FormatAlignRightIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Justify">
              <IconButton
                size="small"
                onClick={() =>
                  editor.chain().focus().setTextAlign('justify').run()
                }
                color={
                  editor.isActive({textAlign: 'justify'})
                    ? 'primary'
                    : 'default'
                }
              >
                <FormatAlignJustifyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        )}

        {/* Formatting */}
        {showFormattingGroup && (
          <div className="html-editor-toolbar__group">
            {has('bold') && (
              <Tooltip title="Bold">
                <IconButton
                  size="small"
                  onClick={() => editor.chain().focus().toggleBold().run()}
                  color={editor.isActive('bold') ? 'primary' : 'default'}
                >
                  <FormatBoldIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {has('italic') && (
              <Tooltip title="Italic">
                <IconButton
                  size="small"
                  onClick={() => editor.chain().focus().toggleItalic().run()}
                  color={editor.isActive('italic') ? 'primary' : 'default'}
                >
                  <FormatItalicIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {has('underline') && (
              <Tooltip title="Underline">
                <IconButton
                  size="small"
                  onClick={() => editor.chain().focus().toggleUnderline().run()}
                  color={editor.isActive('underline') ? 'primary' : 'default'}
                >
                  <FormatUnderlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {has('strikethrough') && (
              <Tooltip title="Strikethrough">
                <IconButton
                  size="small"
                  onClick={() => editor.chain().focus().toggleStrike().run()}
                  color={editor.isActive('strike') ? 'primary' : 'default'}
                >
                  <StrikethroughSIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {has('superscript') && (
              <Tooltip title="Superscript">
                <IconButton
                  size="small"
                  onClick={() =>
                    editor.chain().focus().toggleSuperscript().run()
                  }
                  color={editor.isActive('superscript') ? 'primary' : 'default'}
                >
                  <span style={{fontSize: 12, fontWeight: 'bold'}}>
                    X<sup>2</sup>
                  </span>
                </IconButton>
              </Tooltip>
            )}

            {has('removeFormat') &&
              (has('bold') ||
                has('italic') ||
                has('underline') ||
                has('strikethrough') ||
                has('superscript')) && (
                <div className="html-editor-toolbar__divider" />
              )}

            {has('removeFormat') && (
              <Tooltip title="Remove Format">
                <IconButton
                  size="small"
                  onClick={() =>
                    editor.chain().focus().unsetAllMarks().clearNodes().run()
                  }
                >
                  <FormatClearIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </div>
        )}

        {/* Insert */}
        {showInsertGroup && (
          <div className="html-editor-toolbar__group">
            {has('link') && (
              <Tooltip title="Link">
                <IconButton
                  size="small"
                  onClick={handleLink}
                  color={editor.isActive('link') ? 'primary' : 'default'}
                >
                  <LinkIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {has('mediaEmbed') && (
              <Tooltip title="Media Embed">
                <IconButton size="small" onClick={handleMediaEmbed}>
                  <VideoLibraryIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {has('imageUpload') && (
              <Tooltip title="Upload Image">
                <IconButton size="small" onClick={handleImageClick}>
                  <ImageIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{display: 'none'}}
              onChange={handleFileChange}
            />
            {has('htmlEmbed') && (
              <Tooltip title="HTML Embed">
                <IconButton size="small" onClick={handleHtmlEmbed}>
                  <CodeIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </div>
        )}

        {/* Lists / Indent */}
        {showListGroup && (
          <div className="html-editor-toolbar__group">
            {has('bulletedList') && (
              <Tooltip title="Bullet List">
                <IconButton
                  size="small"
                  onClick={() =>
                    editor.chain().focus().toggleBulletList().run()
                  }
                  color={editor.isActive('bulletList') ? 'primary' : 'default'}
                >
                  <FormatListBulletedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {has('numberedList') && (
              <Tooltip title="Ordered List">
                <IconButton
                  size="small"
                  onClick={() =>
                    editor.chain().focus().toggleOrderedList().run()
                  }
                  color={editor.isActive('orderedList') ? 'primary' : 'default'}
                >
                  <FormatListNumberedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {has('outdent') && (
              <Tooltip title="Decrease Indent">
                <IconButton
                  size="small"
                  onClick={() => {
                    if (editor.isActive('listItem')) {
                      editor.chain().focus().liftListItem('listItem').run()
                    } else {
                      editor.chain().focus().outdent().run()
                    }
                  }}
                >
                  <FormatIndentDecreaseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {has('indent') && (
              <Tooltip title="Increase Indent">
                <IconButton
                  size="small"
                  onClick={() => {
                    if (editor.isActive('listItem')) {
                      editor.chain().focus().sinkListItem('listItem').run()
                    } else {
                      editor.chain().focus().indent().run()
                    }
                  }}
                >
                  <FormatIndentIncreaseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </div>
        )}

        {/* Block / Table */}
        {showBlockGroup && (
          <div className="html-editor-toolbar__group">
            {has('blockQuote') && (
              <Tooltip title="Blockquote">
                <IconButton
                  size="small"
                  onClick={() =>
                    editor.chain().focus().toggleBlockquote().run()
                  }
                  color={editor.isActive('blockquote') ? 'primary' : 'default'}
                >
                  <FormatQuoteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {has('insertTable') && (
              <Tooltip title="Insert Table">
                <IconButton size="small" onClick={handleInsertTable}>
                  <TableChartIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </div>
        )}

        {/* ObvioVariable dropdown */}
        {showVariableGroup && (
          <div className="html-editor-toolbar__group">
            <select
              className="html-editor-toolbar__select"
              value=""
              onChange={handleVariableChange}
            >
              <option value="" disabled>
                Merge Codes
              </option>
              {variables!.map((v) => (
                <option key={v.value} value={v.value}>
                  {v.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Insert Custom Button */}
        {showCustomButtonGroup && (
          <div className="html-editor-toolbar__group">
            <Tooltip title="Insert Button">
              <IconButton size="small" onClick={handleInsertCustomButton}>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 'bold',
                    border: '1px solid currentColor',
                    borderRadius: 3,
                    padding: '1px 4px',
                    lineHeight: 1.3,
                  }}
                >
                  BTN
                </span>
              </IconButton>
            </Tooltip>
          </div>
        )}

        {/* Theme Toggle */}
        {showThemeGroup && (
          <div className="html-editor-toolbar__group">
            <select
              className="html-editor-toolbar__select"
              value={theme}
              onChange={(e) => onThemeChange(e.target.value as EditorTheme)}
            >
              <option value="Auto">Auto</option>
              <option value="Light">Light</option>
              <option value="Dark">Dark</option>
            </select>
          </div>
        )}
      </div>

      {/* Custom Button contextual toolbar */}
      {isCustomButtonSelected && <CustomButtonToolbar editor={editor} />}
    </>
  )
}
